// ---------------------------------------------------------------------------
// The one drag gesture this screen has.
//
// The lobby drags two different things — a round along the plan, and a player
// into a team — and Isaac has praised the first one by name, so the second had
// to feel like the same gesture rather than like a second one written later.
// What is shared is the bookkeeping, which is all of the part that is easy to
// get wrong: a press that has not moved far enough to be a drag, the window
// listeners that have to outlive the element under the finger, the click that
// arrives after a drag and means nothing, and the release that has to clean up
// whether it came from a finger lifting or from the browser taking the gesture
// away. What is not shared is where the thing lands, which is the caller's
// geometry and stays with the caller.
//
// **The listeners go on `window`, not on the element.** A pointer leaves the
// chip it picked up in the first few pixels of the drag, and a listener on the
// element stops hearing about it. They are mounted while a press is live and
// torn down when it ends, which is why `active` is state rather than a ref.
//
// **The callbacks are read through a ref.** They close over the caller's
// current props on every render, and listing them as effect dependencies would
// rebind the whole trio mid-drag every time the plan or the roster changed
// underneath — which is every time the server broadcasts.
//
// TWO WAYS A PRESS BECOMES A DRAG, AND THE DIFFERENCE IS WHETHER THE THING
// UNDER THE FINGER SCROLLS.
//
// A round chip sets `touch-action: none` and lives in a well that does not
// scroll, so movement alone can start the drag: there is no other gesture the
// movement could have meant. A seat row is in the list that scrolls the whole
// lobby, so movement means scrolling until proven otherwise, and starting a
// drag from it would take the list's own gesture away. That one is `holdMs`:
// hold still for long enough and the press becomes a drag; move first and the
// press is abandoned and the list scrolls as it always did.
//
// **A hold is cancelled by movement, never promoted by it.** The tolerance
// during the wait is the same `threshold` a movement drag needs to start, so a
// finger that shakes on a phone still gets its drag and a finger that sets off
// down the page never does.
//
// **A promoted touch drag blocks `touchmove`, and the listener is registered
// BEFORE any of this rather than at the moment it is needed.**
//
// `touch-action` is read once, when the gesture begins, so it cannot be changed
// to `none` after the hold fires — by then the browser has already decided this
// touch may pan. A non-passive `touchmove` listener that calls
// `preventDefault()` is the only thing left that stops the page moving under a
// row somebody is carrying.
//
// It used to be added at promotion, and Isaac reported what that produced on a
// phone: the row followed his finger and the page scrolled with it, so the row
// never moved relative to anything. **Registering it mid-gesture is too late.**
// WebKit works out which parts of the page can cancel a touch from the touch
// event regions it has already sent to the scrolling thread, and a gesture that
// began with no non-passive `touchmove` listener on its path is fast-pathed
// straight to the scroller — every `preventDefault()` after that is ignored. The
// listener has to exist before the finger lands, not before it is used.
//
// So it is mounted for the hook's lifetime and **does nothing until a drag is
// actually carrying something**. A press that stays a press, or one abandoned by
// movement during the hold, runs through a handler that returns immediately, and
// the list scrolls exactly as it always did. Only the seat drag pays for it: the
// round chips pass no `holdMs`, sit in a well that does not scroll, and get the
// same guarantee declaratively from `touch-action: none`, which is the reliable
// way to say this when you are allowed to say it at all.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/** Where the pointer is, in client coordinates, plus the event it came from. */
export interface DragPoint {
  x: number;
  y: number;
  /**
   * The move itself. The player drag reads `target` off it to find the drop
   * zone under the finger; the round drag never looks.
   */
  event: PointerEvent;
}

export interface PointerDragOptions<T> {
  /** Movement in CSS pixels before a press counts as a drag. */
  threshold: number;
  /**
   * How long a touch has to be held still before the press becomes a drag, or
   * null when movement alone starts it. Ignored for a mouse, which has no
   * competing gesture to protect.
   */
  holdMs?: number | null;
  /** Once, at the moment the press becomes a drag. */
  onStart?: (payload: T, point: DragPoint) => void;
  /** Every move from the promotion onward, including the promotion itself. */
  onMove: (payload: T, point: DragPoint) => void;
  /**
   * The release, however it arrived. `dragged` is false for a press that never
   * became a drag, which is the ordinary tap and is the caller's to handle.
   */
  onEnd: (payload: T, dragged: boolean) => void;
}

export interface PointerDragHandle<T> {
  /** `onPointerDown={begin(payload)}`. */
  begin: (payload: T) => (event: ReactPointerEvent) => void;
  /** True while a press is live, whether or not it has become a drag. */
  active: boolean;
  /**
   * Whether the click now arriving is the tail of a drag, and should be thrown
   * away. Reading it clears it, so the next real click gets through.
   */
  swallowClick: () => boolean;
}

interface Press<T> {
  payload: T;
  x: number;
  y: number;
  dragging: boolean;
  /** Waiting on the hold timer. Cleared when it fires or when it is abandoned. */
  waiting: boolean;
  touch: boolean;
}

export function usePointerDrag<T>({
  threshold,
  holdMs = null,
  onStart,
  onMove,
  onEnd,
}: PointerDragOptions<T>): PointerDragHandle<T> {
  const [active, setActive] = useState(false);
  const press = useRef<Press<T> | null>(null);
  const swallow = useRef(false);
  const timer = useRef(0);
  /**
   * A promoted TOUCH drag is in flight and the page must not scroll under it.
   *
   * A ref rather than state because the only thing that reads it is a listener
   * registered once, and re-registering that listener is the bug this whole
   * arrangement exists to avoid.
   */
  const carrying = useRef(false);

  // Refreshed every render rather than listed as dependencies below. See the
  // note at the top: a rebind mid-drag is a drag that loses its own press.
  const callbacks = useRef({ onStart, onMove, onEnd });
  useEffect(() => {
    callbacks.current = { onStart, onMove, onEnd };
  });

  // The timer is outside the drag effect, because ending the drag is what tears
  // that effect down and a timer cleared by its own cause never runs.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  /**
   * Registered for the hook's lifetime and inert until a drag is carrying.
   *
   * Stable by construction — `useCallback` with no dependencies — because add
   * and remove have to be handed the same function, and because a listener that
   * comes and goes is the one thing WebKit will not honour. See the note at the
   * top of the file.
   */
  const blockTouch = useCallback((event: TouchEvent) => {
    if (carrying.current) event.preventDefault();
  }, []);

  // Only the gesture that has a scroll to protect pays for this. A drag with no
  // hold is a round chip in a well that does not scroll, and it says so with
  // `touch-action: none` instead.
  const guardsScroll = holdMs !== null;
  useEffect(() => {
    if (!guardsScroll) return;
    window.addEventListener('touchmove', blockTouch, { passive: false });
    return () => window.removeEventListener('touchmove', blockTouch);
  }, [guardsScroll, blockTouch]);

  const promote = useCallback(
    (current: Press<T>, point: DragPoint) => {
      current.waiting = false;
      current.dragging = true;
      // From here the listener above starts refusing the page's scroll. Set
      // before the callbacks, so the very first move of the drag is already
      // covered rather than the second.
      if (current.touch) carrying.current = true;
      callbacks.current.onStart?.(current.payload, point);
      callbacks.current.onMove(current.payload, point);
    },
    [],
  );

  useEffect(() => {
    if (!active) return;

    const finish = () => {
      const current = press.current;
      press.current = null;
      window.clearTimeout(timer.current);
      // The listener stays; only its answer changes. The page scrolls again the
      // instant the finger lifts.
      carrying.current = false;
      setActive(false);
      if (current === null) return;
      // Set before the callback, so a caller that re-renders on the drop still
      // finds the flag in place when the click lands a moment later.
      if (current.dragging) swallow.current = true;
      callbacks.current.onEnd(current.payload, current.dragging);
    };

    const move = (event: PointerEvent) => {
      const current = press.current;
      if (current === null) return;
      const point: DragPoint = { x: event.clientX, y: event.clientY, event };
      const far = Math.hypot(event.clientX - current.x, event.clientY - current.y) >= threshold;

      if (!current.dragging) {
        // Still waiting on a hold: this movement is the list being scrolled, so
        // the press is given up rather than turned into a drag.
        if (current.waiting) {
          if (far) finish();
          return;
        }
        if (!far) return;
        promote(current, point);
        return;
      }

      // Only ever called once the drag is real. Calling it on a press that is
      // still a maybe would cancel the scroll it might turn out to be.
      event.preventDefault();
      callbacks.current.onMove(current.payload, point);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [active, threshold, promote]);

  const begin = (payload: T) => (event: ReactPointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    swallow.current = false;
    // A mouse has nothing to lose by starting on movement: there is no scroll
    // gesture it could be taking away.
    const held = holdMs !== null && event.pointerType !== 'mouse';
    const touch = event.pointerType === 'touch' || event.pointerType === 'pen';
    const current: Press<T> = {
      payload,
      x: event.clientX,
      y: event.clientY,
      dragging: false,
      waiting: held,
      touch,
    };
    press.current = current;
    setActive(true);
    if (!held) return;

    const point: DragPoint = {
      x: event.clientX,
      y: event.clientY,
      event: event.nativeEvent,
    };
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      // The press may already be over: a finger that lifted inside the hold is
      // a tap, and this is the timer it left behind.
      if (press.current !== current || current.dragging) return;
      promote(current, point);
    }, holdMs);
  };

  return {
    begin,
    active,
    swallowClick: () => {
      const was = swallow.current;
      swallow.current = false;
      return was;
    },
  };
}
